import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from '../../hooks/use-toast';
import { Upload, X, FileText, Image as ImageIcon, Save, Loader2, Eye, MapPin, AlertTriangle } from 'lucide-react';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../ui/alert';
import SmartLocationInput from '../SmartLocationInput';
import ImageCropDialog from '../ImageCropDialog';

const TrainerProfileEditor = ({ onSaveAll, externalHasChanges = false }) => {
  const [trainerData, setTrainerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [availableSpecializations, setAvailableSpecializations] = useState([]);
  
  // Image crop dialog state
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [currentImageForCrop, setCurrentImageForCrop] = useState(null);
  const [currentFileForCrop, setCurrentFileForCrop] = useState(null);
  
  // Combined changes check - internal or external
  const anyChanges = hasChanges || externalHasChanges;
  
  const [formData, setFormData] = useState({
    bio: '',
    specializations: [],
    pricePerHour: '',
    age: '',
    phone: '',
    whatsapp: '',
    location: null,
  });

  // Pending uploads (not saved yet)
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [pendingCertificates, setPendingCertificates] = useState([]);
  const [photosToDelete, setPhotosToDelete] = useState([]);
  const [certificatesToDelete, setCertificatesToDelete] = useState([]);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchTrainerData();
    fetchSpecializations();
  }, []);

  const fetchSpecializations = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/proficiency-icons`);
      if (response.ok) {
        const data = await response.json();
        setAvailableSpecializations(data.map(icon => icon.name));
      }
    } catch (error) {
      console.error('Error fetching specializations:', error);
    }
  };

  const fetchTrainerData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainers/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTrainerData(data);
        setFormData({
          bio: data.bio || '',
          specializations: data.specializations || [],
          pricePerHour: data.price_per_hour || data.pricePerHour || '',
          age: data.age || '',
          phone: data.phone || '',
          whatsapp: data.whatsapp || '',
          location: data.location || null,
        });
      }
    } catch (error) {
      console.error('Error fetching trainer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      // Validate
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Error',
          description: 'Please upload image files only',
          variant: 'destructive'
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: `${file.name} is too large (max 5MB)`,
          variant: 'destructive'
        });
        return;
      }

      // Open crop dialog
      const reader = new FileReader();
      reader.onload = (e) => {
        setCurrentImageForCrop(e.target.result);
        setCurrentFileForCrop(file);
        setCropDialogOpen(true);
      };
      reader.readAsDataURL(file);
    });

    e.target.value = ''; // Reset input
  };

  const handleCropComplete = async (croppedBlob) => {
    try {
      // Create a new File object from the cropped blob
      const croppedFile = new File([croppedBlob], currentFileForCrop.name, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      // Create preview URL from blob for immediate display
      const previewUrl = URL.createObjectURL(croppedBlob);
      
      // Create data URL for better compatibility
      const reader = new FileReader();
      reader.onload = (e) => {
        setPendingPhotos(prev => [...prev, {
          file: croppedFile,
          preview: e.target.result, // Use data URL for preview
          isNew: true
        }]);
        setHasChanges(true);
        
        toast({
          title: 'Success',
          description: 'Image adjusted successfully. Remember to save changes!',
        });
      };
      reader.readAsDataURL(croppedBlob);
      
    } catch (error) {
      console.error('Error handling cropped image:', error);
      toast({
        title: 'Error',
        description: 'Failed to process image',
        variant: 'destructive'
      });
    }
  };

  const handleCertificateSelect = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      // Validate
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Error',
          description: 'Only JPG, PNG, WebP, or PDF files allowed',
          variant: 'destructive'
        });
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: `${file.name} is too large (max 10MB)`,
          variant: 'destructive'
        });
        return;
      }

      setPendingCertificates(prev => [...prev, {
        file,
        filename: file.name,
        isNew: true
      }]);
      setHasChanges(true);
    });

    e.target.value = ''; // Reset input
  };

  const handleRemovePendingPhoto = (index) => {
    setPendingPhotos(prev => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleRemovePendingCert = (index) => {
    setPendingCertificates(prev => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleMarkPhotoForDeletion = (photoUrl) => {
    setPhotosToDelete(prev => [...prev, photoUrl]);
    setHasChanges(true);
  };

  const handleMarkCertForDeletion = (certUrl) => {
    setCertificatesToDelete(prev => [...prev, certUrl]);
    setHasChanges(true);
  };

  const handleSaveAll = async () => {
    // Check if location is set - MANDATORY
    if (!formData.location || !formData.location.coordinates) {
      toast({
        title: 'Location Required',
        description: 'Please set your location in the "Your Location / Service Area" section. This is required for clients to find you.',
        variant: 'destructive'
      });
      
      // Scroll to location section
      const locationSection = document.querySelector('[data-section="location"]');
      if (locationSection) {
        locationSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    setSaving(true);
    let successCount = 0;
    let errorCount = 0;
    
    try {
      const token = localStorage.getItem('token');

      // 0. Call external save handlers (Gender, Training Types, etc.)
      if (onSaveAll) {
        console.log('Calling external save handlers...');
        const externalResults = await onSaveAll();
        if (externalResults) {
          if (externalResults.genderSaved) console.log('Gender saved via external handler');
          if (externalResults.trainingTypesSaved) console.log('Training types saved via external handler');
        }
      }

      // 1. Update profile info
      console.log('Updating profile info...');
      const profileResponse = await fetch(`${API_BASE}/api/trainers/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!profileResponse.ok) {
        console.error('Profile update failed:', await profileResponse.text());
        throw new Error('Failed to update profile');
      }
      console.log('Profile info updated successfully');

      // 2. Upload new photos
      console.log(`Uploading ${pendingPhotos.length} photos...`);
      for (const photo of pendingPhotos) {
        try {
          const photoFormData = new FormData();
          photoFormData.append('file', photo.file);

          const response = await fetch(`${API_BASE}/api/trainer/photos`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: photoFormData
          });

          if (response.ok) {
            console.log('Photo uploaded:', photo.file.name);
            successCount++;
          } else {
            const error = await response.text();
            console.error('Photo upload failed:', error);
            errorCount++;
          }
        } catch (err) {
          console.error('Photo upload error:', err);
          errorCount++;
        }
      }

      // 3. Upload new certificates
      console.log(`Uploading ${pendingCertificates.length} certificates...`);
      for (const cert of pendingCertificates) {
        try {
          const certFormData = new FormData();
          certFormData.append('file', cert.file);

          const response = await fetch(`${API_BASE}/api/trainer/certificates`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: certFormData
          });

          if (response.ok) {
            console.log('Certificate uploaded:', cert.filename);
            successCount++;
          } else {
            const error = await response.text();
            console.error('Certificate upload failed:', error);
            errorCount++;
          }
        } catch (err) {
          console.error('Certificate upload error:', err);
          errorCount++;
        }
      }

      // 4. Delete marked photos
      console.log(`Deleting ${photosToDelete.length} photos...`);
      for (const photoUrl of photosToDelete) {
        try {
          const filename = photoUrl.split('/').pop();
          const response = await fetch(`${API_BASE}/api/trainer/photos/${filename}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (response.ok) {
            console.log('Photo deleted:', filename);
          } else {
            console.error('Photo delete failed:', filename);
          }
        } catch (err) {
          console.error('Photo delete error:', err);
        }
      }

      // 5. Delete marked certificates
      console.log(`Deleting ${certificatesToDelete.length} certificates...`);
      for (const certUrl of certificatesToDelete) {
        try {
          const filename = certUrl.split('/').pop();
          const response = await fetch(`${API_BASE}/api/trainer/certificates/${filename}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (response.ok) {
            console.log('Certificate deleted:', filename);
          } else {
            console.error('Certificate delete failed:', filename);
          }
        } catch (err) {
          console.error('Certificate delete error:', err);
        }
      }

      // 6. Mark any pending feedback notifications as read
      console.log('Checking for pending feedback notifications...');
      try {
        const notificationsResponse = await fetch(`${API_BASE}/api/trainer/notifications`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (notificationsResponse.ok) {
          const notifications = await notificationsResponse.json();
          const pendingFeedbackNotifs = notifications.filter(
            n => n.type === 'listing_changes_requested' && !n.read
          );
          
          // Mark all pending feedback notifications as read
          for (const notif of pendingFeedbackNotifs) {
            try {
              await fetch(`${API_BASE}/api/trainer/notifications/${notif.id}/mark-read`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
              });
              console.log('Marked notification as read:', notif.id);
            } catch (err) {
              console.error('Failed to mark notification as read:', err);
            }
          }
        }
      } catch (err) {
        console.error('Error checking/marking notifications:', err);
      }

      // Show result
      if (errorCount === 0) {
        toast({
          title: 'Success',
          description: 'All changes saved successfully!'
        });
      } else {
        toast({
          title: 'Partially Saved',
          description: `${successCount} items saved, ${errorCount} failed. Check console for details.`,
          variant: 'destructive'
        });
      }

      // Wait a moment before refreshing
      console.log('Waiting 1 second before refresh...');
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Refresh data
      console.log('Refreshing trainer data...');
      await fetchTrainerData();
      
      // Reset state ONLY after successful refresh
      setPendingPhotos([]);
      setPendingCertificates([]);
      setPhotosToDelete([]);
      setCertificatesToDelete([]);
      setHasChanges(false);
      
      console.log('All done!');

    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save profile',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  // Calculate current items (excluding marked for deletion)
  const currentPhotos = (trainerData?.photos || []).filter(p => !photosToDelete.includes(p));
  const currentCertificates = (trainerData?.certificates || []).filter(c => !certificatesToDelete.includes(c?.url || c));

  // Debug logging
  console.log('TrainerData:', trainerData);
  console.log('Current Photos:', currentPhotos);
  console.log('Pending Photos:', pendingPhotos);
  console.log('Current Certificates:', currentCertificates);
  console.log('Pending Certificates:', pendingCertificates);

  return (
    <div className="space-y-6">
      {/* Action Buttons - Fixed at top */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg border-2 border-red-200 dark:border-red-800">
        <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
          <div className="flex items-center gap-2">
            {anyChanges && (
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                Unsaved Changes
              </Badge>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full items-center sm:justify-start">
            <Button
              onClick={async () => {
                // Fetch latest data before showing preview
                await fetchTrainerData();
                setPreviewOpen(true);
              }}
              variant="outline"
              className="w-full sm:w-auto"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview Profile
            </Button>
            <Button
              onClick={handleSaveAll}
              disabled={saving || !anyChanges}
              className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900 text-white font-bold"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save All Changes
                </>
              )}
            </Button>

          </div>
        </div>
      </div>

      {/* Profile Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Save className="w-5 h-5" />
            Profile Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="bio">Bio / About Me</Label>
            <Textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => handleFormChange('bio', e.target.value)}
              placeholder="Tell clients about yourself, your experience, and training philosophy..."
              className="min-h-[120px]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pricePerHour">Price per Hour ($)</Label>
              <Input
                id="pricePerHour"
                type="number"
                value={formData.pricePerHour}
                onChange={(e) => handleFormChange('pricePerHour', e.target.value)}
                placeholder="50"
              />
            </div>

            <div>
              <Label htmlFor="age">Age</Label>
              <Input
                id="age"
                type="number"
                min="18"
                max="100"
                value={formData.age || ''}
                onChange={(e) => handleFormChange('age', e.target.value)}
                placeholder="e.g., 30"
              />
              <p className="text-xs text-gray-500 mt-1">Used for age range filtering</p>
            </div>

            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div>
              <Label htmlFor="whatsapp">WhatsApp Number</Label>
              <Input
                id="whatsapp"
                value={formData.whatsapp}
                onChange={(e) => handleFormChange('whatsapp', e.target.value)}
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>

          {/* Location Section - MANDATORY */}
          <div className="space-y-3" data-section="location">
            <Label className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-red-500" />
              Your Location / Service Area
              <Badge variant="destructive" className="ml-2 text-xs">Required</Badge>
            </Label>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Set your location so clients can find you. This is required for your profile to appear in search results.
            </p>
            
            {/* Location Required Alert */}
            {!formData.location?.coordinates && (
              <Alert variant="destructive" className="border-red-300 bg-red-50 dark:bg-red-900/20">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Location Required</AlertTitle>
                <AlertDescription>
                  You must set your location to save your profile. Clients search for trainers by location - without this, you won't appear in search results.
                </AlertDescription>
              </Alert>
            )}
            
            <SmartLocationInput
              onPlaceSelect={(place) => {
                console.log('Place selected:', place);  // Debug log
                if (place) {
                  const locationData = {
                    formatted_address: place.formatted_address,
                    street: place.street,
                    place_id: place.place_id,
                    coordinates: place.coordinates,
                    city: place.city,
                    province: place.province,
                    country: place.country,
                    postal_code: place.postal_code
                  };
                  setFormData(prev => ({ ...prev, location: locationData }));
                  setHasChanges(true);
                  console.log('Location updated, hasChanges set to true');
                } else {
                  setFormData(prev => ({ ...prev, location: null }));
                  setHasChanges(true);
                }
              }}
              defaultValue={formData.location?.formatted_address || ''}
              placeholder="Enter your address or city..."
              showCurrentLocation={true}
            />
            {formData.location?.formatted_address && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span><strong>Selected:</strong> {formData.location.formatted_address}</span>
                </p>
                {formData.location.city && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1 ml-6">
                    City: {formData.location.city}{formData.location.province ? `, ${formData.location.province}` : ''}
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <Label>Specializations</Label>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Select your training specializations (click to toggle)
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {availableSpecializations.map((spec) => {
                const isSelected = formData.specializations.includes(spec);
                return (
                  <Badge
                    key={spec}
                    variant={isSelected ? "default" : "outline"}
                    className={`cursor-pointer transition-all ${
                      isSelected 
                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                    onClick={() => {
                      const newSpecs = isSelected
                        ? formData.specializations.filter(s => s !== spec)
                        : [...formData.specializations, spec];
                      handleFormChange('specializations', newSpecs);
                    }}
                  >
                    {spec}
                    {isSelected && <span className="ml-1">✓</span>}
                  </Badge>
                );
              })}
            </div>
            {formData.specializations.length === 0 && (
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
                ⚠️ Please select at least one specialization
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Photos Section */}
      <Card id="training-photos-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Training Photos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Upload photos of your training sessions, gym, or fitness achievements.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Existing photos */}
            {currentPhotos.map((photo, idx) => (
              <div key={`existing-${idx}`} className="relative group">
                <img
                  src={`${API_BASE}${photo}`}
                  alt={`Training photo ${idx + 1}`}
                  className="w-full h-32 object-cover rounded-lg"
                />
                <button
                  onClick={() => handleMarkPhotoForDeletion(photo)}
                  className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
                {photosToDelete.includes(photo) && (
                  <div className="absolute inset-0 bg-red-500 bg-opacity-50 flex items-center justify-center rounded-lg">
                    <span className="text-white font-bold">Will be deleted</span>
                  </div>
                )}
              </div>
            ))}

            {/* Pending photos */}
            {pendingPhotos.map((photo, idx) => (
              <div key={`pending-${idx}`} className="relative group">
                <img
                  src={photo.preview}
                  alt={`New photo ${idx + 1}`}
                  className="w-full h-32 object-cover rounded-lg border-2 border-green-500"
                />
                <button
                  onClick={() => handleRemovePendingPhoto(idx)}
                  className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="absolute bottom-2 left-2">
                  <Badge className="bg-green-500 text-white text-xs">NEW</Badge>
                </div>
              </div>
            ))}
            
            {/* Upload button */}
            <label className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-red-500 hover:bg-red-50 transition-colors">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoSelect}
                className="hidden"
              />
              <Upload className="w-8 h-8 text-gray-400" />
              <span className="text-sm text-gray-500 mt-2">Add Photos</span>
            </label>
          </div>

          <p className="text-xs text-gray-500">
            Maximum 5MB per photo. Supported: JPG, PNG, WebP
          </p>
        </CardContent>
      </Card>

      {/* Certificates Section */}
      <Card id="certifications-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Certifications & Qualifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Upload your training certifications, qualifications, or licenses.
          </p>

          <div className="space-y-3">
            {/* Existing certificates */}
            {currentCertificates.map((cert, idx) => (
              <div key={`existing-cert-${idx}`} className={`flex items-center justify-between p-3 rounded-lg group ${
                certificatesToDelete.includes(cert.url) 
                  ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700' 
                  : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600'
              }`}>
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{cert.filename || `Certificate ${idx + 1}`}</p>
                    {cert.uploaded_at && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Uploaded: {new Date(cert.uploaded_at).toLocaleDateString()}
                      </p>
                    )}
                    {certificatesToDelete.includes(cert.url) && (
                      <Badge variant="destructive" className="text-xs mt-1">Will be deleted</Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(`${API_BASE}${cert.url}`, '_blank')}
                  >
                    View
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleMarkCertForDeletion(cert.url)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}

            {/* Pending certificates */}
            {pendingCertificates.map((cert, idx) => (
              <div key={`pending-cert-${idx}`} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{cert.filename}</p>
                    <Badge className="bg-green-500 text-white text-xs mt-1">NEW - Not saved yet</Badge>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleRemovePendingCert(idx)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}

            {/* Upload button */}
            <label className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-red-500 hover:bg-red-50 transition-colors">
              <input
                type="file"
                accept="image/*,.pdf"
                multiple
                onChange={handleCertificateSelect}
                className="hidden"
              />
              <Upload className="w-6 h-6 text-gray-400 mr-2" />
              <span className="text-sm text-gray-600">Add Certificates</span>
            </label>
          </div>

          <p className="text-xs text-gray-500">
            Maximum 10MB per file. Supported: JPG, PNG, WebP, PDF
          </p>
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Profile Preview - How Users Will See You</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Bio Preview */}
            <div className="bg-gradient-to-r from-red-50 to-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-xl mb-3 text-red-900">About Me</h3>
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {formData.bio || '⚠️ No bio added yet - Add your bio to attract more clients!'}
              </p>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border-2 border-red-200 shadow-sm">
                <h4 className="font-semibold text-sm text-gray-600 mb-1">Price per Hour</h4>
                <p className="text-2xl font-bold text-red-600">
                  {formData.pricePerHour ? `$${formData.pricePerHour}` : '⚠️ Not set'}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border-2 border-red-200 shadow-sm">
                <h4 className="font-semibold text-sm text-gray-600 mb-1">Specializations</h4>
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.specializations.length > 0 ? (
                    formData.specializations.map((spec, idx) => (
                      <Badge key={spec} variant="secondary" className="bg-red-100 text-red-700">
                        {spec}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">None</span>
                  )}
                </div>
              </div>
              
              {/* Gender Display */}
              {trainerData?.gender && (
                <div className="bg-white p-4 rounded-lg border-2 border-red-200 shadow-sm">
                  <h4 className="font-semibold text-sm text-gray-600 mb-1">Gender</h4>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className="bg-red-100 text-red-700 hover:bg-red-200 text-base py-1 px-3">
                      {trainerData.gender === 'male' && '♂️ Male'}
                      {trainerData.gender === 'female' && '♀️ Female'}
                      {trainerData.gender === 'non-binary' && '⚧️ X (non-binary)'}
                    </Badge>
                  </div>
                </div>
              )}
              
              {/* Age Display */}
              {trainerData?.age && (
                <div className="bg-white p-4 rounded-lg border-2 border-orange-200 shadow-sm">
                  <h4 className="font-semibold text-sm text-gray-600 mb-1">Age</h4>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200 text-base py-1 px-3">
                      🎂 {trainerData.age} years old
                    </Badge>
                  </div>
                </div>
              )}
              
              {/* Training Types Display */}
              {trainerData?.training_types && trainerData.training_types.length > 0 && (
                <div className="bg-white p-4 rounded-lg border-2 border-blue-200 shadow-sm">
                  <h4 className="font-semibold text-sm text-gray-600 mb-1">Training Type</h4>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {trainerData.training_types.map((type, idx) => (
                      <Badge key={type} className="bg-blue-100 text-blue-700 hover:bg-blue-200 text-base py-1 px-3">
                        {type === 'Online' && '🌐 Online'}
                        {type === 'In Person' && '🏋️ In Person'}
                        {type === 'Both' && '🌐🏋️ Both'}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {formData.phone && (
                <div className="bg-white p-4 rounded-lg border-2 border-blue-200 shadow-sm">
                  <h4 className="font-semibold text-sm text-gray-600 mb-1">Phone</h4>
                  <p className="text-lg font-medium">{formData.phone}</p>
                </div>
              )}
              {formData.whatsapp && (
                <div className="bg-white p-4 rounded-lg border-2 border-green-200 shadow-sm">
                  <h4 className="font-semibold text-sm text-gray-600 mb-1">WhatsApp</h4>
                  <p className="text-lg font-medium">{formData.whatsapp}</p>
                </div>
              )}
            </div>

            {/* Photos Preview */}
            <div className="bg-white p-6 rounded-lg border-2 border-gray-200 shadow-sm">
              <h3 className="font-bold text-xl mb-4 flex items-center gap-2">
                <ImageIcon className="w-6 h-6 text-red-600" />
                Training Photos
                <Badge className="ml-2">
                  {currentPhotos.length + pendingPhotos.length} photos
                </Badge>
              </h3>
              
              {(currentPhotos.length > 0 || pendingPhotos.length > 0) ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {/* Current Photos */}
                  {currentPhotos.map((photo, idx) => (
                    <div key={`current-${idx}`} className="relative group">
                      <img
                        src={`${API_BASE}${photo}`}
                        alt={`Photo ${idx + 1}`}
                        className="w-full h-32 object-cover rounded-lg shadow-md"
                      />
                      <div className="absolute top-2 left-2">
                        <Badge className="bg-blue-500 text-white text-xs">Saved</Badge>
                      </div>
                    </div>
                  ))}
                  
                  {/* Pending Photos */}
                  {pendingPhotos.map((photo, idx) => (
                    <div key={`pending-${idx}`} className="relative group">
                      <img
                        src={photo.preview}
                        alt={`New photo ${idx + 1}`}
                        className="w-full h-32 object-cover rounded-lg shadow-md border-4 border-green-500"
                      />
                      <div className="absolute top-2 left-2">
                        <Badge className="bg-green-500 text-white text-xs animate-pulse">NEW</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">⚠️ No photos added yet</p>
                  <p className="text-gray-400 text-xs mt-1">Add photos to make your profile more attractive</p>
                </div>
              )}
            </div>

            {/* Certificates Preview */}
            <div className="bg-white p-6 rounded-lg border-2 border-gray-200 shadow-sm">
              <h3 className="font-bold text-xl mb-4 flex items-center gap-2">
                <FileText className="w-6 h-6 text-red-600" />
                Certifications & Qualifications
                <Badge className="ml-2">
                  {currentCertificates.length + pendingCertificates.length} certificates
                </Badge>
              </h3>
              
              {(currentCertificates.length > 0 || pendingCertificates.length > 0) ? (
                <div className="space-y-3">
                  {/* Current Certificates */}
                  {currentCertificates.map((cert, idx) => (
                    <div key={`current-cert-${idx}`} className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{cert.filename || `Certificate ${idx + 1}`}</p>
                        {cert.uploaded_at && (
                          <p className="text-xs text-gray-500">
                            Uploaded: {new Date(cert.uploaded_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <Badge className="bg-blue-500 text-white text-xs">Saved</Badge>
                    </div>
                  ))}
                  
                  {/* Pending Certificates */}
                  {pendingCertificates.map((cert, idx) => (
                    <div key={`pending-cert-${idx}`} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border-2 border-green-500">
                      <FileText className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{cert.filename}</p>
                        <p className="text-xs text-gray-500">Ready to be uploaded</p>
                      </div>
                      <Badge className="bg-green-500 text-white text-xs animate-pulse">NEW</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">⚠️ No certificates added yet</p>
                  <p className="text-gray-400 text-xs mt-1">Add certifications to build trust with clients</p>
                </div>
              )}
            </div>

            {/* Warning if unsaved */}
            {hasChanges && (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                <p className="text-yellow-800 font-semibold text-center">
                  ⚠️ Remember to click "SAVE ALL CHANGES" to make these updates live!
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Crop Dialog */}
      <ImageCropDialog
        open={cropDialogOpen}
        onClose={() => setCropDialogOpen(false)}
        imageSrc={currentImageForCrop}
        onCropComplete={handleCropComplete}
        aspectRatio={4 / 3}
      />
    </div>
  );
};

export default TrainerProfileEditor;
